// src/components/login/legal-page-layout.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// The shared shell for the Terms and Privacy pages. Both of them move by this
// one file moving: the page keeps its exact structure and every word, and the
// box around it becomes Kit 07 item 164, `LegalDocument`.
//
// What changed, and each of these was a defect rather than a preference:
//
//   * The sticky header painted `rgba(var(--card-rgb), 0.95)`. `--card-rgb` is
//     not declared anywhere in the build, so the whole colour was invalid and
//     the header was TRANSPARENT with a blur behind it, in both themes.
//   * "← Back" used a Unicode arrow as an icon, which F10 forbids outright.
//     It is `BackLink`, which draws a Lucide chevron.
//   * The header carried `z-index: 50`, a literal outside the named ladder.
//     It is `--z-sticky`.
//   * The six content components carried thirty-one raw px values and eleven
//     legacy variables in style attributes, none of them visible to any check.
//     They are classes in `legal-page.css`, which is held to zero.
//
// Item 164's key is "No control floats over the text", so the Back control is
// in a header ABOVE the document rather than pinned over it.
import { ReactNode } from "react";
import { UpmodeLogo } from "./upmode-logo";
import { BackLink, LegalDocument } from "../kit";

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
  onNavigateBack?: () => void;
}

export function LegalPageLayout({
  title,
  lastUpdated,
  children,
  onNavigateBack,
}: LegalPageLayoutProps) {
  return (
    <div className="upm-legal-page" data-kit>
      <header className="upm-legal-page__bar">
        <UpmodeLogo width={140} height={32} />
        {onNavigateBack && (
          <BackLink href="#" onClick={(e) => { e.preventDefault(); onNavigateBack(); }}>
            Back
          </BackLink>
        )}
      </header>

      <main className="upm-legal-page__main">
        <LegalDocument title={title} updated={`Last updated ${lastUpdated}`}>
          {children}
        </LegalDocument>
      </main>

      <footer className="upm-legal-page__foot">
        <span className="k-meta">
          © {new Date().getFullYear()} Upmode AI. All rights reserved.
        </span>
      </footer>
    </div>
  );
}

/** A numbered top-level section. */
export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="upm-legal__section">
      <h2 className="upm-legal__h2">{title}</h2>
      <div className="upm-legal__prose">{children}</div>
    </section>
  );
}

/** A nested section inside one of the above. */
export function LegalSubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="upm-legal__subsection">
      <h3 className="upm-legal__h3">{title}</h3>
      <div className="upm-legal__prose">{children}</div>
    </div>
  );
}

export function LegalParagraph({ children }: { children: ReactNode }) {
  return <p className="upm-legal__p">{children}</p>;
}

export function LegalList({ children }: { children: ReactNode }) {
  return <ul className="upm-legal__list">{children}</ul>;
}

export function LegalListItem({ children }: { children: ReactNode }) {
  return <li>{children}</li>;
}
